import findReadWriteRecipient from '..//actions/findReadWriteRecipient';
import addMentionAddedRecipient from '../actions/addMentionAddedRecipient';
import addMentionTuple from '../actions/addMentionTuple';
import addSelectedRecipientToWell from '../actions/addSelectedRecipientToWell';
import removeMentionAddedRecipient from '../actions/removeMentionAddedRecipient';
import removeMentionTuple from '../actions/removeMentionTuple';
import setIsSearching from '../actions/setIsSearching';
import setIsSuggesting from '../actions/setIsSuggesting';
import setSuggestedContacts from '../actions/setSuggestedContacts';
import shiftAtMentionsHighlight from '../actions/shiftAtMentionsHighlight';
import { disposeAtMentionsPopup, showAtMentionsPopup } from '../components/AtMentionsResult';
import { AT_MENTION_ELEMENT_ID_PREFIX } from '../constants';
import AtMentionsViewState from '../store/schema/AtMentionsViewState';
import { logUsage } from 'owa-analytics';
import { PluginViewStateAdapter } from 'owa-editor';
import removePersonaFromWell from 'owa-readwrite-recipient-well/lib/actions/removePersonaFromWell';
import FindRecipientPersonaType from 'owa-recipient-common/lib/store/schema/FindRecipientPersonaType';
import ReadWriteRecipientViewState from 'owa-recipient-common/lib/store/schema/ReadWriteRecipientViewState';
import RecipientWellWithFindControlViewState from 'owa-recipient-common/lib/store/schema/RecipientWellWithFindControlViewState';
import compareFindRecipientPersonaType from 'owa-recipient-common/lib/utils/compareFindRecipientPersonaType';
import getDisplayNameFromFindRecipientPersonaType from 'owa-recipient-common/lib/utils/getDisplayNameFromFindRecipientPersonaType';
import EmailAddressWrapper from 'owa-service/lib/contract/EmailAddressWrapper';
import MentionActionWrapper from 'owa-service/lib/contract/MentionActionWrapper';
import { PickerDataProvider } from 'roosterjs-plugin-picker';

const AT_MENTION_SEARCH_DELAY = 300;
const RESIZE_EVENT_NAME = 'resize';
const MAX_DATABASE_ENTRIES = 23;

const styles = require('../components/MentionComposeStyles.scss');

export interface MentionPluginDataProviderInterface extends PickerDataProvider {
    getMentionsWrappersForSend: (fromWrapper: EmailAddressWrapper) => MentionActionWrapper[];
    getPendingTagsForSave: () => string[];
}

export default class MentionPluginDataProvider implements MentionPluginDataProviderInterface {
    private timer: NodeJS.Timer;
    private popupDiv: HTMLDivElement;
    private commitMention: (element: HTMLElement) => void;
    private setIsSuggesting: (isSuggesting: boolean) => void;

    constructor(
        private adapter: PluginViewStateAdapter<AtMentionsViewState>,
        private targetRecipientWell: RecipientWellWithFindControlViewState,
        private onRecipientAdded: () => void,
        private canModifyRecipients: boolean,
        private getAllRecipients: () => ReadWriteRecipientViewState[]
    ) {}

    onInitalize(
        commitMentionCallback: (element: HTMLElement) => void,
        isSuggestingCallback: (isSuggesting: boolean) => void
    ) {
        window.addEventListener(RESIZE_EVENT_NAME, this.onScroll);
        const divElement = document.createElement('div');
        divElement.id = 'atMentionsSearchPopup';
        document.body.appendChild(divElement);
        this.popupDiv = divElement;
        this.commitMention = commitMentionCallback;
        this.setIsSuggesting = isSuggestingCallback;
    }

    onDispose() {
        window.removeEventListener(RESIZE_EVENT_NAME, this.onScroll);
        clearTimeout(this.timer);
        this.timer = null;
        if (this.popupDiv) {
            disposeAtMentionsPopup(this.popupDiv);
            document.body.removeChild(this.popupDiv);
        }
        this.popupDiv = null;
    }

    onIsSuggestingChanged = (isSuggesting: boolean) => {
        const viewState = this.adapter();

        setIsSuggesting(viewState, isSuggesting);
        if (isSuggesting && !viewState.queryString) {
            setSuggestedContacts(viewState, this.getAllRecipients());
        }
    };

    queryStringUpdated = (queryString: string) => {
        const viewState = this.adapter();

        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
        if (queryString) {
            setIsSearching(viewState, true, queryString);
            this.timer = setTimeout(() => {
                findReadWriteRecipient(viewState, queryString, false, this.getAllRecipients());
                this.timer = null;
            }, AT_MENTION_SEARCH_DELAY);
        } else {
            setSuggestedContacts(viewState, this.getAllRecipients());
        }
    };

    selectOption = () => {
        const viewState = this.adapter();

        if (viewState.selectedRecipientIndex == viewState.findResultSet.length) {
            findReadWriteRecipient(viewState, viewState.queryString, true, this.getAllRecipients());
        } else {
            this.addRecipient();
        }
    };

    shiftHighlight = (isDown: boolean) => {
        shiftAtMentionsHighlight(this.adapter(), isDown);
    };

    onRemove = (nodeRemoved: Node, isBackspace: boolean) => {
        if (isBackspace && nodeRemoved.textContent.split(' ').length > 1) {
            return this.deleteLastSegment(nodeRemoved);
        } else {
            this.deleteAtMention(nodeRemoved);
            return null;
        }
    };

    setCursorPoint = (targetPoint: { x: number; y: number }, buffer: number) => {
        showAtMentionsPopup(targetPoint, buffer, this.adapter(), this.popupDiv, this.addRecipient);
    };

    getSelectedIndex = (): number => {
        return this.adapter().selectedRecipientIndex;
    };

    onScroll = () => {
        if (this.adapter().isSuggesting) {
            this.setIsSuggesting(false);
        }
    };

    private addRecipient = (recipient: FindRecipientPersonaType = null) => {
        const viewState = this.adapter();

        if (!recipient) {
            recipient = viewState.findResultSet[viewState.selectedRecipientIndex];
        }

        this.commitMention(this.createInlineFromPersona(recipient));

        if (!this.doesWellContainRecipient(recipient) && this.canModifyRecipients) {
            addSelectedRecipientToWell(viewState, this.targetRecipientWell, recipient);
            if (recipient.EmailAddress) {
                addMentionAddedRecipient(viewState, recipient);
            }
        }

        setIsSuggesting(viewState, false);

        if (this.onRecipientAdded) {
            this.onRecipientAdded();
        }

        logUsage('MentionsAdded');
    };

    private doesWellContainRecipient(recipientToCheck?: FindRecipientPersonaType) {
        const viewState = this.adapter();

        if (!recipientToCheck) {
            recipientToCheck = viewState.findResultSet[viewState.selectedRecipientIndex];
        }
        const filterFunc = (value: ReadWriteRecipientViewState) => {
            return compareFindRecipientPersonaType(value.persona, recipientToCheck);
        };

        return this.getAllRecipients().some(filterFunc);
    }

    private createInlineFromPersona(targetRecipient: FindRecipientPersonaType): HTMLAnchorElement {
        const anchorElement = document.createElement('a');
        const atMentionId = this.generateAtMentionId();
        const targetName = getDisplayNameFromFindRecipientPersonaType(targetRecipient);
        anchorElement.id = atMentionId;
        anchorElement.innerText = '@' + targetName;

        // We add both our styling for React, as well as the hard coded styling for MVVM.
        anchorElement.className = styles.mentionsCompose + ' mention ms-bgc-nlr ms-fcl-b';

        const email = targetRecipient.EmailAddress.EmailAddress;
        anchorElement.href = 'mailto:' + email;

        addMentionTuple(this.adapter(), atMentionId, targetRecipient);
        return anchorElement;
    }

    private generateAtMentionId(): string {
        return AT_MENTION_ELEMENT_ID_PREFIX + Math.round(Math.random() * 1000000).toString();
    }

    private deleteLastSegment(node: Node) {
        const splitContents = node.textContent.split(' ');
        splitContents.pop();
        node.textContent = splitContents.join(' ').trim();
        return node;
    }

    private getIdValue(node: Node): string {
        const element = node as Element;
        return element.attributes && element.attributes.getNamedItem('id')
            ? (element.attributes.getNamedItem('id').value as string)
            : null;
    }

    private deleteAtMention(node: Node) {
        const nodeId = this.getIdValue(node);
        const viewState = this.adapter();
        const { atMentionTuples, mentionsAddedRecipients } = viewState;

        if (atMentionTuples[nodeId]) {
            if (atMentionTuples[nodeId].EmailAddress) {
                const indexToRemove = mentionsAddedRecipients.indexOf(
                    atMentionTuples[nodeId].EmailAddress.EmailAddress
                );
                if (indexToRemove > -1) {
                    // If someone @mentioned someone multiple times, we should not remove until it's the last one.
                    const duplicated = Object.keys(atMentionTuples).some(
                        (key): boolean => {
                            if (key != nodeId && atMentionTuples[key]) {
                                return (
                                    atMentionTuples[key].EmailAddress &&
                                    atMentionTuples[key].EmailAddress.EmailAddress ==
                                        atMentionTuples[nodeId].EmailAddress.EmailAddress
                                );
                            } else {
                                return false;
                            }
                        }
                    );
                    if (!duplicated && this.canModifyRecipients) {
                        removePersonaFromWell(this.targetRecipientWell, atMentionTuples[nodeId]);
                        removeMentionAddedRecipient(viewState, indexToRemove);
                    }
                }
            }
            removeMentionTuple(viewState, nodeId);
        }
    }

    public getMentionsWrappersForSend(fromWrapper: EmailAddressWrapper): MentionActionWrapper[] {
        const { atMentionTuples } = this.adapter();
        const mentionsWrappers: MentionActionWrapper[] = [];

        Object.keys(atMentionTuples).forEach(key => {
            const persona = atMentionTuples[key];
            mentionsWrappers.push({
                Mentioned: persona.EmailAddress,
                CreatedBy: fromWrapper,
            });
        });
        if (mentionsWrappers.length > 0) {
            const totalMentions = mentionsWrappers.length;
            logUsage('MentionsTotalSend', {
                totalMentions: (totalMentions >= MAX_DATABASE_ENTRIES
                    ? MAX_DATABASE_ENTRIES
                    : totalMentions
                ).toString(),
            });
        }

        return mentionsWrappers;
    }

    public getPendingTagsForSave(): string[] {
        const pendingTags: string[] = [];
        const { atMentionTuples, mentionsAddedRecipients } = this.adapter();

        Object.keys(atMentionTuples).forEach(key => {
            const persona = atMentionTuples[key];
            pendingTags.push(
                JSON.stringify({
                    b: key,
                    a: JSON.stringify({
                        _type: 'MentionActionWrapper:#Exchange',
                        Mentioned: persona.EmailAddress,
                    }),
                })
            );
        });
        if (mentionsAddedRecipients.length > 0) {
            pendingTags.push(
                JSON.stringify({
                    b: 'MentionAddedRecipients',
                    a: JSON.stringify(mentionsAddedRecipients),
                })
            );
        }
        if (pendingTags.length > 0) {
            const totalMentions = Object.keys(atMentionTuples).length;
            const numberAddedViaMentions = mentionsAddedRecipients.length;
            logUsage('MentionsTotalSave', {
                totalMentions: (totalMentions >= MAX_DATABASE_ENTRIES
                    ? MAX_DATABASE_ENTRIES
                    : totalMentions
                ).toString(),
                numberAddedViaMentions: (numberAddedViaMentions >= MAX_DATABASE_ENTRIES
                    ? MAX_DATABASE_ENTRIES
                    : numberAddedViaMentions
                ).toString(),
            });
        }

        return pendingTags;
    }
}
